import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { BreadTransaction } from "breadcommon";
import { PreferencesContext } from "./PreferencesContext";
import { TransactionsContext } from "./TransactionsContext";
import { BudgetsContext } from "./BudgetsContext";
import dayjs from "dayjs";

export const SUPPORTED_CURRENCIES = [
  "USD", "EUR", "GBP", "CAD", "JPY", "AUD", "BRL", "BGN", "CHF", "CNY",
  "CZK", "DKK", "HKD", "HUF", "IDR", "ILS", "INR", "ISK", "KRW", "MXN",
  "MYR", "NOK", "NZD", "PHP", "PLN", "RON", "SEK", "SGD", "THB", "TRY", "ZAR"
];

interface ExchangeRatesContextType {
  convert: (amount: number, fromCurrency: string, date: dayjs.Dayjs | string | null) => number;
  convertLatest: (amount: number, fromCurrency: string) => number;
  convertTransactionsToDefaultCurrency: (transactions: BreadTransaction[]) => BreadTransaction[];
  convertTransactionsToSpecificCurrency: (transactions: BreadTransaction[], targetCurrency: string) => BreadTransaction[];
  isLoading: boolean;
  /** Whether the latest rates, used by convertLatest, are still loading. */
  isLoadingLatest: boolean;
}

const ExchangeRatesContext = createContext<ExchangeRatesContextType | null>(null);

export const useExchangeRates = () => {
  const context = useContext(ExchangeRatesContext);
  if (!context) {
    throw new Error("useExchangeRates must be used within an ExchangeRatesContextProvider");
  }
  return context;
};

export function ExchangeRatesContextProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const preferences = useContext(PreferencesContext);
  const transactions = useContext(TransactionsContext);
  const budgets = useContext(BudgetsContext);

  const defaultCurrency = preferences?.web?.default_currency ?? "USD";

  const [latestRates, setLatestRates] = useState<Record<string, number>>({});
  const [historicalRates, setHistoricalRates] = useState<Record<string, Record<string, number>>>({});
  const [isLoadingLatest, setIsLoadingLatest] = useState(true);
  const [isLoadingHistorical, setIsLoadingHistorical] = useState(false);

  // Load initial cached rates from LocalStorage
  useEffect(() => {
    // Reset state first to avoid stale rates from previous defaultCurrency
    setLatestRates({});
    setHistoricalRates({});
    setIsLoadingLatest(true);

    // 1. Load latest rates cache
    const latestCacheKey = `moolah_latest_rates_${defaultCurrency}`;
    const cachedLatest = localStorage.getItem(latestCacheKey);
    if (cachedLatest) {
      try {
        const parsed = JSON.parse(cachedLatest);
        const ageMs = Date.now() - parsed.timestamp;
        // Cache is valid for 12 hours
        if (ageMs < 12 * 60 * 60 * 1000) {
          setLatestRates(parsed.rates);
          setIsLoadingLatest(false);
        }
      } catch (e) {
        console.error("Failed to parse cached latest rates", e);
      }
    }

    // 2. Load historical rates cache
    const historicalCacheKey = `moolah_historical_rates_${defaultCurrency}`;
    const cachedHistorical = localStorage.getItem(historicalCacheKey);
    if (cachedHistorical) {
      try {
        setHistoricalRates(JSON.parse(cachedHistorical));
      } catch (e) {
        console.error("Failed to parse cached historical rates", e);
      }
    }
  }, [defaultCurrency]);

  // Fetch latest rates if expired or missing
  useEffect(() => {
    const fetchLatest = async () => {
      if (defaultCurrency === "") return;
      
      const latestCacheKey = `moolah_latest_rates_${defaultCurrency}`;
      const cachedLatest = localStorage.getItem(latestCacheKey);
      if (cachedLatest) {
        try {
          const parsed = JSON.parse(cachedLatest);
          const ageMs = Date.now() - parsed.timestamp;
          if (ageMs < 12 * 60 * 60 * 1000) {
            return; // Already loaded and fresh
          }
        } catch (e) {}
      }

      setIsLoadingLatest(true);
      try {
        const response = await fetch(`https://api.frankfurter.dev/v1/latest?base=${defaultCurrency}`);
        if (!response.ok) {
          throw new Error(`Frankfurter API returned status ${response.status}`);
        }
        const data = await response.json();
        const rates = data.rates || {};
        // Frankfurter latest rate for base currency is 1
        rates[defaultCurrency] = 1.0;

        setLatestRates(rates);
        localStorage.setItem(latestCacheKey, JSON.stringify({
          timestamp: Date.now(),
          rates
        }));
      } catch (e) {
        console.error("Failed to fetch latest exchange rates", e);
      } finally {
        setIsLoadingLatest(false);
      }
    };

    fetchLatest();
  }, [defaultCurrency]);

  // Fetch missing historical rates based on active transactions
  useEffect(() => {
    const fetchMissingHistorical = async () => {
      if (!transactions || transactions.size === 0) return;

      // 1. Identify all foreign currencies from both transactions and budgets
      const missingDates: string[] = [];
      const foreignCurrencies = new Set<string>();

      for (const tx of Array.from(transactions.values())) {
        const txCurrency = tx.currency_code;
        if (txCurrency && txCurrency !== defaultCurrency && SUPPORTED_CURRENCIES.includes(txCurrency)) {
          foreignCurrencies.add(txCurrency);
        }
      }

      for (const budget of Array.from(budgets.values())) {
        const budgetCurrency = budget.currency;
        if (budgetCurrency && budgetCurrency !== defaultCurrency && SUPPORTED_CURRENCIES.includes(budgetCurrency)) {
          foreignCurrencies.add(budgetCurrency);
        }
      }

      // Check which dates are missing rates for any of our needed currencies
      for (const tx of Array.from(transactions.values())) {
        const dateStr = tx.date.format("YYYY-MM-DD");
        for (const curr of Array.from(foreignCurrencies)) {
          if (!historicalRates[dateStr]?.[curr]) {
            missingDates.push(dateStr);
            break;
          }
        }
      }

      if (missingDates.length === 0) return;

      // Sort dates to find the range
      missingDates.sort();
      const minDate = missingDates[0];
      const maxDate = missingDates[missingDates.length - 1];

      setIsLoadingHistorical(true);
      try {
        const response = await fetch(
          `https://api.frankfurter.dev/v1/${minDate}..${maxDate}?base=${defaultCurrency}`
        );
        if (!response.ok) {
          throw new Error(`Frankfurter range API returned status ${response.status}`);
        }
        const data = await response.json();
        const apiRates = data.rates || {};

        // 2. Interpolate weekend/holiday gaps
        let currentDay = dayjs(minDate);
        const endDay = dayjs(maxDate);
        let lastKnownRates: Record<string, number> = {};

        // Find the first available rate as our fallback if the range starts on a weekend
        const firstAvailableDate = Object.keys(apiRates).sort()[0];
        if (firstAvailableDate) {
          lastKnownRates = { ...apiRates[firstAvailableDate] };
        }

        const newHistoricalRatesPatch: Record<string, Record<string, number>> = {};

        while (currentDay.isBefore(endDay) || currentDay.isSame(endDay, "day")) {
          const dateStr = currentDay.format("YYYY-MM-DD");
          if (apiRates[dateStr]) {
            lastKnownRates = { ...lastKnownRates, ...apiRates[dateStr] };
          }

          if (Object.keys(lastKnownRates).length > 0) {
            newHistoricalRatesPatch[dateStr] = { 
              ...lastKnownRates,
              [defaultCurrency]: 1.0 // Base currency is always 1.0
            };
          }
          currentDay = currentDay.add(1, "day");
        }

        // 3. Merge patch and save to LocalStorage
        setHistoricalRates((prev) => {
          const updated = { ...prev, ...newHistoricalRatesPatch };
          localStorage.setItem(
            `moolah_historical_rates_${defaultCurrency}`,
            JSON.stringify(updated)
          );
          return updated;
        });
      } catch (e) {
        console.error("Failed to fetch historical exchange rates", e);
      } finally {
        setIsLoadingHistorical(false);
      }
    };

    // Only run if latest rates/base currency are initialized
    if (!isLoadingLatest) {
      fetchMissingHistorical();
    }
  }, [transactions, budgets, defaultCurrency, isLoadingLatest, historicalRates]);

  // Conversion: Latest rates (used for current account balances)
  const convertLatest = useCallback((amount: number, fromCurrency: string): number => {
    if (!fromCurrency || fromCurrency === defaultCurrency) {
      return amount;
    }
    const rate = latestRates[fromCurrency];
    if (rate !== undefined && rate !== 0) {
      return amount / rate;
    }
    return amount; // Fallback to raw value if rate not found
  }, [latestRates, defaultCurrency]);

  // Conversion: Historical rates (used for transactions)
  const convert = useCallback((
    amount: number,
    fromCurrency: string,
    date: dayjs.Dayjs | string | null
  ): number => {
    if (!fromCurrency || fromCurrency === defaultCurrency) {
      return amount;
    }
    
    if (!date) {
      return convertLatest(amount, fromCurrency);
    }

    const dateStr = typeof date === "string" ? date : date.format("YYYY-MM-DD");
    const dailyRates = historicalRates[dateStr];
    if (dailyRates) {
      const rate = dailyRates[fromCurrency];
      if (rate !== undefined && rate !== 0) {
        return amount / rate;
      }
    }

    // Fallback to latest rate if historical rate is missing for that specific day
    return convertLatest(amount, fromCurrency);
  }, [historicalRates, convertLatest, defaultCurrency]);

  // Conversion: Map convert list of transactions to user's default currency
  const convertTransactionsToDefaultCurrency = useCallback((txs: BreadTransaction[]): BreadTransaction[] => {
    return txs.map((tx) => {
      if (!tx.currency_code || tx.currency_code === defaultCurrency) {
        return tx;
      }
      const convertedAmount = convert(tx.amount, tx.currency_code, tx.date);
      return {
        ...tx,
        amount: convertedAmount,
        currency_code: defaultCurrency
      };
    });
  }, [convert, defaultCurrency]);

  // Conversion: Map convert list of transactions to a specific target currency
  const convertTransactionsToSpecificCurrency = useCallback((
    txs: BreadTransaction[],
    targetCurrency: string
  ): BreadTransaction[] => {
    return txs.map((tx) => {
      if (!tx.currency_code || tx.currency_code === targetCurrency) {
        return tx;
      }
      // 1. Convert to user default (base) currency
      const amountInBase = convert(tx.amount, tx.currency_code, tx.date);
      // 2. Convert from base currency to targetCurrency
      const targetRateInBase = convert(1, targetCurrency, tx.date);
      const convertedAmount = targetRateInBase === 0 ? amountInBase : amountInBase / targetRateInBase;
      return {
        ...tx,
        amount: convertedAmount,
        currency_code: targetCurrency,
      };
    });
  }, [convert]);

  const isLoading = isLoadingLatest || isLoadingHistorical;

  const value = useMemo(() => ({
    convert,
    convertLatest,
    convertTransactionsToDefaultCurrency,
    convertTransactionsToSpecificCurrency,
    isLoading,
    isLoadingLatest
  }), [convert, convertLatest, convertTransactionsToDefaultCurrency, convertTransactionsToSpecificCurrency, isLoading, isLoadingLatest]);

  return (
    <ExchangeRatesContext.Provider value={value}>
      {children}
    </ExchangeRatesContext.Provider>
  );
}
